'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { LuCreditCard } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import { useAsyncAction } from '@/core/hooks/use-async-action'
import { toast } from '@/core/lib/toast'
import {
  readBillingMethods,
  readBrandpaySession,
  removeBillingMethod,
} from '@/features/veluvit/services/billing.service'
import { runPaymentLayer } from '@/features/veluvit/services/payment-layer'
import {
  openBrandpayCardRegistration,
  openBrandpaySettings,
} from '@/features/veluvit/services/toss-sdk'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { BillingMethods } from '@/features/veluvit/schemas/billing.schema'

export default function BillingMethodsPanel({ ownerId }: { ownerId: string }) {
  const identity = useSessionStore((state) => state.identity)
  const owner = useSessionStore((state) => state.data?.user?.id)
  if (owner !== ownerId) return null
  return (
    <BillingMethodsContent
      key={`${ownerId}:${identity}`}
      ownerId={ownerId}
      identity={identity}
    />
  )
}

function BillingMethodsContent({
  ownerId,
  identity,
}: {
  ownerId: string
  identity: number
}) {
  const [data, setData] = useState<BillingMethods | null>(null)
  const [error, setError] = useState('')
  const { busy, run } = useAsyncAction()
  const [deleting, setDeleting] = useState<string | null>(null)
  const controller = useRef<AbortController | null>(null)
  const active = useRef(false)
  const current = useCallback(
    () =>
      active.current &&
      useSessionStore.getState().identity === identity &&
      useSessionStore.getState().data?.user?.id === ownerId,
    [ownerId, identity]
  )
  const visibleData = data

  const load = useCallback(
    (signal?: AbortSignal) =>
      readBillingMethods(ownerId, signal)
        .then((next) => {
          if (current() && !signal?.aborted) {
            setData(next)
            setError('')
          }
          return next
        })
        .catch((cause: unknown) => {
          if (current() && !signal?.aborted)
            setError(
              cause instanceof Error
                ? cause.message
                : '등록한 카드를 불러오지 못했어요.'
            )
          return null
        }),
    [current, ownerId]
  )

  useEffect(() => {
    active.current = true
    const initial = new AbortController()
    controller.current = initial
    void load(initial.signal)
    return () => {
      active.current = false
      initial.abort()
      controller.current?.abort()
    }
  }, [load])

  async function perform(kind: 'register' | 'settings') {
    if (!data?.brandpayEnabled || !current()) return
    await run(async () => {
      controller.current?.abort()
      const operation = new AbortController()
      controller.current = operation
      setError('')
      try {
        const before = new Set(data.methods.map((method) => method.id))
        const session = await readBrandpaySession(ownerId, operation.signal)
        if (!current() || operation.signal.aborted) return
        await runPaymentLayer(operation.signal, (options) =>
          kind === 'register'
            ? openBrandpayCardRegistration(session, current, options)
            : openBrandpaySettings(session, current, options)
        )
        if (!current() || operation.signal.aborted) return
        const next = await load(operation.signal)
        if (
          kind === 'register' &&
          next?.methods.some((method) => !before.has(method.id))
        )
          toast.success('카드를 등록했어요.')
      } catch (cause) {
        if (
          current() &&
          !operation.signal.aborted &&
          !(cause instanceof Error && /cancel|취소/i.test(cause.message))
        ) {
          setError(
            cause instanceof Error
              ? cause.message
              : '카드 관리 화면을 열지 못했어요.'
          )
        }
      } finally {
        if (controller.current === operation) controller.current = null
      }
    })
  }

  async function remove(id: string) {
    if (!current()) return
    await run(async () => {
      controller.current?.abort()
      const operation = new AbortController()
      controller.current = operation
      setError('')
      try {
        await removeBillingMethod(ownerId, id, operation.signal)
        if (!current() || operation.signal.aborted) return
        setDeleting(null)
        await load(operation.signal)
      } catch (cause) {
        if (current() && !operation.signal.aborted)
          setError(
            cause instanceof Error ? cause.message : '카드를 삭제하지 못했어요.'
          )
      } finally {
        if (controller.current === operation) controller.current = null
      }
    })
  }

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-c20 font-bold">등록한 카드</h2>
        <p className="mt-2 text-c14 leading-6 text-description">
          등록한 카드는 결제할 때 직접 선택할 수 있어요. 등록만으로 결제되지
          않아요.
        </p>
      </div>
      {!visibleData && !error && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {visibleData && (
        <ul className="divide-y divide-border">
          {visibleData.methods.map((method) => (
            <li key={method.id} className="flex items-center gap-3 py-4">
              <LuCreditCard
                aria-hidden="true"
                className="size-5 flex-none text-description"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-c15 font-semibold">
                  {method.label}
                </p>
                <p className="mt-1 text-c13 text-description">
                  {method.last4 ? `끝 번호 ${method.last4}` : method.brand}
                  {method.status !== 'active' && ' · 사용 중지'}
                </p>
              </div>
              {deleting === method.id ? (
                <div className="flex flex-none gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setDeleting(null)}
                  >
                    취소
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={busy}
                    onClick={() => void remove(method.id)}
                  >
                    삭제
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => setDeleting(method.id)}
                >
                  삭제
                </Button>
              )}
            </li>
          ))}
          {visibleData.methods.length === 0 && (
            <li className="py-4 text-c14 text-description">
              등록한 카드가 없어요.
            </li>
          )}
        </ul>
      )}
      {visibleData?.brandpayEnabled ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => void perform('register')}
          >
            카드 등록
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => void perform('settings')}
          >
            간편결제 설정
          </Button>
        </div>
      ) : (
        visibleData && (
          <p className="text-c14 leading-6 text-description">
            카드 등록을 준비하고 있어요. 지금은 일회성 결제를 이용해 주세요.
          </p>
        )
      )}
      {error && (
        <p role="alert" className="text-c14 leading-6 text-error">
          {error}
        </p>
      )}
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={() => {
          controller.current?.abort()
          const operation = new AbortController()
          controller.current = operation
          void load(operation.signal)
        }}
      >
        목록 새로고침
      </Button>
    </section>
  )
}
